import { Container } from '@/components/Container';
import { useTranslation } from '@/hooks/useTranslation';

/* ═══════════════════════════════════════════
   TRILHAS — Dados validados hardcoded
   ═══════════════════════════════════════════ */

interface Trilha {
  id: string;
  nome: string;
  distancia: string;
  nivel: 'facil' | 'moderado';
  descricao: string;
  tempoIda: string;
  pontoPartida: string;
  calcado: string;
  alertaGruta?: boolean;
}

const trilhas: Trilha[] = [
  {
    id: 'encantadas-gruta',
    nome: 'Encantadas → Gruta das Encantadas',
    distancia: '650 m (ida)',
    nivel: 'facil',
    descricao:
      'Trilha curta e plana, perfeita para iniciantes. Sai da vila e chega à Gruta em cerca de 10 minutos.',
    tempoIda: '10 min',
    pontoPartida: 'Centro de Encantadas',
    calcado: 'Sandália com strap ou tênis',
    alertaGruta: true,
  },
  {
    id: 'nova-brasilia-farol',
    nome: 'Nova Brasília → Farol das Conchas',
    distancia: '1,7 km',
    nivel: 'facil',
    descricao:
      'Caminho tranquilo pela Praia do Farol até a base do farol. Acesso fácil para todas as idades.',
    tempoIda: '25 min',
    pontoPartida: 'Centro de Nova Brasília',
    calcado: 'Sandália com strap ou tênis',
  },
  {
    id: 'nova-brasilia-fortaleza',
    nome: 'Nova Brasília → Fortaleza de Nossa Senhora dos Prazeres',
    distancia: '3,7 km',
    nivel: 'moderado',
    descricao:
      'Trilha com trechos de areia solta e raízes. Leva cerca de 50 min e passa por praias desertas.',
    tempoIda: '50 min',
    pontoPartida: 'Centro de Nova Brasília',
    calcado: 'Tênis fechado',
  },
  {
    id: 'nova-brasilia-encantadas',
    nome: 'Nova Brasília → Encantadas (trilha completa)',
    distancia: '5 km',
    nivel: 'moderado',
    descricao:
      'Cruza a ilha de ponta a ponta. Leva 1h30–2h. Paisagens variadas: mangue, praia e mata atlântica.',
    tempoIda: '1h30–2h',
    pontoPartida: 'Centro de Nova Brasília',
    calcado: 'Tênis fechado',
  },
];

const nivelConfig = {
  facil: {
    label: 'Fácil',
    cor: 'bg-teal-50 text-teal-deep border-teal-200',
  },
  moderado: {
    label: 'Moderado',
    cor: 'bg-amber-50 text-amber-700 border-amber-200',
  },
};

export function TrilhasSection() {
  const { t } = useTranslation();

  return (
    <section id="trilhas" className="py-16 sm:py-20 bg-white">
      <Container>
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.trails.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.trails.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.trails.subtitle')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {trilhas.map(trilha => (
            <div key={trilha.id} className="bg-sand-50 rounded-2xl border border-sand-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={`text-xs font-body font-bold px-2.5 py-0.5 rounded-full border ${nivelConfig[trilha.nivel].cor}`}
                >
                  {nivelConfig[trilha.nivel].label}
                </span>
                <span className="font-body text-xs text-warm-gray">{trilha.distancia}</span>
              </div>
              <h3 className="font-display text-xl text-warm-dark mb-2">
                {trilha.nome.toUpperCase()}
              </h3>
              <p className="font-body text-sm text-warm-gray leading-relaxed">{trilha.descricao}</p>

              {/* Metadados da trilha */}
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="inline-flex items-center gap-1 bg-sand-50 border border-sand-200 rounded-full px-3 py-1 font-body text-xs text-warm-gray">
                  <span role="img" aria-label="Tempo">
                    ⏱️
                  </span>{' '}
                  {trilha.tempoIda}
                </span>
                <span className="inline-flex items-center gap-1 bg-sand-50 border border-sand-200 rounded-full px-3 py-1 font-body text-xs text-warm-gray">
                  <span role="img" aria-label="Calçado">
                    👟
                  </span>{' '}
                  {trilha.calcado}
                </span>
              </div>

              {/* Alerta específico para a Gruta das Encantadas */}
              {trilha.alertaGruta && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 mt-3">
                  <span role="img" aria-label="Alerta" className="text-base flex-shrink-0">
                    ⚠️
                  </span>
                  <p className="font-body text-xs text-warm-gray">
                    <strong className="text-warm-dark">Atenção:</strong> Visite a Gruta na maré
                    baixa. Na maré alta a entrada pode ficar submersa.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 max-w-2xl mx-auto bg-teal-50 border border-teal-200 rounded-xl p-5 flex gap-4">
          <span className="text-2xl flex-shrink-0">🥾</span>
          <div>
            <p className="font-body text-sm font-semibold text-warm-dark mb-1">
              {t('guide.trails.tipTitle', 'Dica do guia')}
            </p>
            <p className="font-body text-sm text-warm-gray">
              {t(
                'guide.trails.tipText',
                'Leve água, protetor solar e tênis de trilha. As trilhas de areia exigem mais esforço do que parecem, e o sol pode ser intenso mesmo no inverno.'
              )}
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}
